import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';

/**
 *  KÉSVE RÖGZÍTETT MULASZTÁSOK  ·  Nkt. 54. § (1)
 *
 *  „A tanuló mulasztással összefüggő adatait a mulasztás napján, haladéktalanul rögzíteni kell a
 *  tanulmányi rendszerben.” Ez a nézet azokat az órákat sorolja fel, amelyeken van hiányzó, és
 *  amelyeket a tanár az óra napjánál később könyvelt – tanáronkénti összesítővel. A számítás és a
 *  jogosultság a kiszolgálóé (backend/mulasztas-ertesites.js › kesveKimutatas, végpont: /api/mulasztas-ertesites/kesve):
 *  az admin az egész intézményt is látja, az osztályfőnök a saját osztályát (és ott csak a saját
 *  tanulóit számoljuk hiányzónak). Csak olvas.
 *
 *  ÁTVÉTELBŐL SZÁRMAZÓ KÉSÉS (2026-11-02): a gépi könyvelés, a tömeges (egy másodpercben sok óra)
 *  könyvelés és a napló használatba vétele előtti napok órái alapból kimaradnak – a kiszolgáló
 *  megmondja, mennyi és miért (`kihagyva`); az „Átvételből származók is” kapcsolóval jelölve látszanak.
 */

export interface KesveSor {
  id: string; datum: string; ora: number | null; hossz: number; osztaly: string | null; csoport: string | null;
  tantargy: string; tanar: string; tanar_nev: string; helyettesites: boolean;
  konyvelte: string | null; konyvelte_nev: string | null; konyvelve: string; keses_nap: number; hianyzo_db: number;
  egy_menetben: number;
  /** Ha átvételből származik: az ok kódja (KesveKihagyasOk.kod), különben null. */
  atvetel: string | null;
}
export interface KesveKihagyasOk { kod: string; nev: string; magyarazat: string; ora_db: number; hianyzo_db: number; }
export interface KesveKihagyva {
  ora_db: number; hianyzo_db: number; okok: KesveKihagyasOk[];
  naplo_kezdete: { tanev: number; nap: string }[]; tomeges_kuszob: number;
}
export interface KesveTanar {
  tanar: string; tanar_nev: string; ora_db: number; hianyzo_db: number; max_keses: number; atlag_keses: number; utolso: string;
  atvetel_db?: number;
}

@Component({
  selector: 'app-of-kesve-mulasztas',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './of-kesve-mulasztas.component.html',
  styleUrls: ['./of-kesve-mulasztas.component.scss'],
})
export class OfKesveMulasztasComponent implements OnChanges {
  @Input() intezmeny = '';
  @Input() osztaly = '';
  @Input() tanev: number | null = null;
  /** Adminnál választható az egész intézmény is (a kiszolgáló úgyis ellenőrzi). */
  @Input() admin = false;

  hatokor: 'osztaly' | 'intezmeny' = 'osztaly';
  tol = '';
  ig = '';
  tanar = '';
  betolt = false;
  hiba = '';
  sorok: KesveSor[] = [];
  tanarok: KesveTanar[] = [];
  osszesen = { ora_db: 0, hianyzo_db: 0, tanar_db: 0 };
  csonkitva = false;
  jogalap = 'Nkt. 54. § (1)';
  /** Az átvételből származó sorok is (jelölve) – alapból kimaradnak. */
  atvetellel = false;
  kihagyva: KesveKihagyva = { ora_db: 0, hianyzo_db: 0, okok: [], naplo_kezdete: [], tomeges_kuszob: 30 };

  constructor(private http: HttpClient) {}

  ngOnChanges(): void { this.betoltes(); }

  betoltes(): void {
    if (!this.intezmeny) return;
    const p = new URLSearchParams();
    if (this.hatokor === 'osztaly' || !this.admin) {
      if (!this.osztaly) { this.sorok = []; this.tanarok = []; return; }
      p.set('osztaly', this.osztaly);
    }
    if (this.tol) p.set('tol', this.tol);
    if (this.ig) p.set('ig', this.ig);
    if (this.tanar) p.set('tanar', this.tanar);
    if (this.tanev) p.set('tanev', String(this.tanev));
    if (this.atvetellel) p.set('atvetel', '1');
    this.betolt = true;
    this.hiba = '';
    this.http.get<any>(`/api/mulasztas-ertesites/kesve/${encodeURI(this.intezmeny)}?${p.toString()}`).subscribe({
      next: (r) => {
        this.sorok = Array.isArray(r?.sorok) ? r.sorok : [];
        this.tanarok = Array.isArray(r?.tanarok) ? r.tanarok : [];
        this.osszesen = r?.osszesen || { ora_db: 0, hianyzo_db: 0, tanar_db: 0 };
        this.csonkitva = !!r?.csonkitva;
        this.jogalap = r?.jogalap || this.jogalap;
        this.kihagyva = {
          ora_db: Number(r?.kihagyva?.ora_db) || 0, hianyzo_db: Number(r?.kihagyva?.hianyzo_db) || 0,
          okok: Array.isArray(r?.kihagyva?.okok) ? r.kihagyva.okok : [],
          naplo_kezdete: Array.isArray(r?.kihagyva?.naplo_kezdete) ? r.kihagyva.naplo_kezdete : [],
          tomeges_kuszob: Number(r?.kihagyva?.tomeges_kuszob) || 30,
        };
        //  Az első betöltés a kiszolgáló alapértelmezett időszakát mutatja (tanév eleje – ma).
        if (!this.tol && r?.tol) this.tol = r.tol;
        if (!this.ig && r?.ig) this.ig = r.ig;
        this.betolt = false;
      },
      error: (e) => {
        this.sorok = []; this.tanarok = [];
        this.betolt = false;
        this.hiba = e?.error?.error || 'Nem sikerült lekérdezni a késve rögzített mulasztásokat.';
      },
    });
  }

  tanarValaszt(t: KesveTanar | null): void {
    this.tanar = t && this.tanar !== t.tanar ? t.tanar : '';
    this.betoltes();
  }

  atvetelValt(v: any): void {
    this.atvetellel = !!v;
    this.betoltes();
  }

  /** Az átvétel okának neve a kódjából (a sorok címkéjéhez). */
  atvetelNev(kod: string | null): string {
    if (!kod) return '';
    return this.kihagyva.okok.find((o) => o.kod === kod)?.nev || 'átvétel';
  }

  atvetelMagyarazat(kod: string | null): string {
    return this.kihagyva.okok.find((o) => o.kod === kod)?.magyarazat || '';
  }

  /** A napló használatba vételének napja tanévenként („2026/2027: 2026.09.08.”). */
  get naploKezdeteSzoveg(): string {
    return this.kihagyva.naplo_kezdete.map((k) => `${k.tanev}/${k.tanev + 1}: ${this.pontos(k.nap)}`).join(', ');
  }

  hatokorValt(h: any): void {
    this.hatokor = h === 'intezmeny' ? 'intezmeny' : 'osztaly';
    this.tanar = '';
    this.betoltes();
  }

  get tanarNev(): string {
    return this.tanarok.find((t) => t.tanar === this.tanar)?.tanar_nev || this.tanar;
  }

  /** 'YYYY-MM-DD' → 'YYYY.MM.DD.' */
  pontos(iso: string | null): string {
    return /^\d{4}-\d{2}-\d{2}/.test(iso || '') ? `${iso!.slice(0, 4)}.${iso!.slice(5, 7)}.${iso!.slice(8, 10)}.${iso!.length > 10 ? iso!.slice(10) : ''}` : '';
  }

  kesesSzint(n: number): string {
    if (n >= 8) return 'kv-piros';
    if (n >= 3) return 'kv-narancs';
    return 'kv-sarga';
  }

  sav(s: KesveSor): string {
    if (s.ora == null) return '–';
    return s.hossz > 1 ? `${s.ora}–${s.ora + s.hossz - 1}.` : `${s.ora}.`;
  }

  csv(): void {
    const fej = ['Dátum', 'Óra', 'Osztály', 'Csoport', 'Tantárgy', 'Tanár', 'Könyvelte', 'Könyvelve', 'Késés (nap)', 'Hiányzók', 'Átvételből'];
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const sorok = this.sorok.map((s) => [s.datum, this.sav(s), s.osztaly, s.csoport, s.tantargy, s.tanar_nev,
      s.konyvelte_nev || s.konyvelte, s.konyvelve, s.keses_nap, s.hianyzo_db, this.atvetelNev(s.atvetel)].map(esc).join(';'));
    const blob = new Blob(['﻿' + [fej.map(esc).join(';'), ...sorok].join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `kesve-rogzitett-mulasztasok-${this.tol}-${this.ig}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }
}
